/* eslint-disable no-extra-boolean-cast */
/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-extraneous-dependencies */

// React and core dependencies
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

// Date/time utilities
import { formatDistanceToNow } from "date-fns";
import ReactTimeAgo from "react-time-ago";

// Material UI components
import Grid from "@mui/material/Grid2";
import { ArrowDownward } from "@mui/icons-material";
import ArrowUpward from "@mui/icons-material/ArrowUpward";

// Layout and UI components
import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "components/DashboardNavbar";
import Footer from "components/Footer";
import MDBox from "components/MDBox";
import Loader from "components/Loader";
import LocationUserPartner from "components/locations/LocationUserPartner";

// Chart components
import ReportsLineChart from "components/Charts/LineCharts/ReportsLineChart";
import ReportsBarChart from "components/Charts/BarCharts/ReportsBarChart";

// Form and schema
import form from "pages/users/edit-user/schema/form";

// Redux actions
import setAlert from "store/actions/alertActions";
import {
  clearErrors,
  setLoading,
  getDashboardStats,
  getTopViewedItems,
  getPartnerDashboard,
  getCompanionAnalytics,
} from "store/actions/statsActions";
import { getUserPartnerLocations, setLocation } from "store/actions/locationAction";

// Dashboard components
import MDTypography from "components/MDTypography";
import MultiLocationUserPartner from "components/locations/MultiLocationUserPartner";
import OrdersDataTable from "../sales/components/OrdersDataTable";
import TopViewedItems from "../sales/components/TopViewedItems";
import BookingsDataTable from "../sales/components/BookingsDataTable";
import AdminSummaryStatistics from "./components/AdminSummaryStatistics";
import PartnerSummaryStatistics from "./components/PartnerSummaryStatistics";
import LocationRequests from "../../content/locations/edit-location/components/LocationRequests";

const Analytics = () => {
  const { location } = form.formField;

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.users.user);
  const adminDashboard = useSelector((state) => state.stats.adminDashboard);
  const topItems = useSelector((state) => state.stats.topItems);

  const error = useSelector((state) => state.stats.error);
  const loading = useSelector((state) => state.stats.loading);
  const companionAnalytics = useSelector((state) => state.stats.companionAnalytics);

  // const [firstRun, setFirstRun] = useState(false);
  const [locationId, setLocationId] = useState("");
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);

  // Stable empty array reference to prevent unnecessary re-renders
  const emptyLocationIds = useMemo(() => [], []);
  // Refs to prevent duplicate fetching on mount/param flaps
  const didFetchLocationsRef = useRef(false);
  const lastCompanionKeyRef = useRef(null);
  const renderAudiReportDescription = () => {
    if (adminDashboard?.auReport?.totalVisitors) {
      return <p>Number of visitors last month: {adminDashboard.auReport.totalVisitors}</p>;
    }

    if (adminDashboard?.auReport?.dataExist) {
      return <p>Last 24 Hours</p>;
    }

    return (
      <p>
        <span style={{ color: "red" }}>No Data Available</span>
      </p>
    );
  };

  const renderDescription = () => {
    if (!adminDashboard?.dailySales?.dataExist) {
      return (
        <p>
          <span style={{ color: "red" }}>No Data Available</span>
        </p>
      );
    }

    if (adminDashboard.dailySales.change < 0) {
      return (
        <p>
          <span style={{ color: "red" }}>
            <ArrowDownward /> {Math.abs(adminDashboard.dailySales.change?.toFixed(2))}%
          </span>{" "}
          decrease in today&#39;s sales.
        </p>
      );
    }

    return (
      <p>
        <span>
          <ArrowUpward /> {adminDashboard.dailySales.change?.toFixed(2)}%
        </span>{" "}
        increase in today&#39;s sales.
      </p>
    );
  };

  const handleLocationChange = useCallback((_, val) => {
    setLocationId(val?._id);
    dispatch(setLocation(val?._id));
  }, []);

  useEffect(() => {
    if (!user) return;
    dispatch(setLoading());
    if (user.role === "admin") {
      dispatch(getDashboardStats(timeZone));
      dispatch(getTopViewedItems());
      return;
    }

    if (user.role === "partner") {
      // Fetch partner locations exactly once
      if (!didFetchLocationsRef.current) {
        dispatch(getUserPartnerLocations());
        didFetchLocationsRef.current = true;
      }

      // Initialize default single location if applicable
      if (user?.locations?.length > 0 && !locationId && !user?.partnerPower) {
        setLocationId(user?.locations[0]?._id);
      }

      // Build a stable key for companion analytics to avoid duplicates
      const companionKey = JSON.stringify({
        partnerPower: !!user?.partnerPower,
        sel: selectedLocationIds?.length > 0 ? selectedLocationIds : "ALL",
        locationId: !user?.partnerPower ? locationId : undefined,
      });

      if (lastCompanionKeyRef.current !== companionKey) {
        lastCompanionKeyRef.current = companionKey;
        if (!!user?.partnerPower) {
          if (selectedLocationIds.length > 0) {
            dispatch(getCompanionAnalytics(selectedLocationIds));
            dispatch(getTopViewedItems(selectedLocationIds));
          } else {
            dispatch(getCompanionAnalytics([]));
            dispatch(getTopViewedItems());
          }
        } else if (locationId) {
          // single-location partner
          dispatch(getPartnerDashboard(locationId, timeZone));
          dispatch(getCompanionAnalytics(locationId));
          dispatch(getTopViewedItems(locationId));
        }
      }
    }
  }, [user, locationId, selectedLocationIds]);

  // ------useEffect-------
  useEffect(() => {
    if (error) {
      dispatch(setAlert(error, "error"));
      dispatch(clearErrors());
    }
    // eslint-disable-next-line
  }, [error, setAlert]);

  // Keep global spinner until core datasets are ready (avoid flashing zeros)
  const isGlobalLoading = useMemo(() => {
    if (!user) return true;
    if (loading) return true;
    if (user.role === "admin") {
      return !adminDashboard || !topItems;
    }
    // partner
    if (!!user?.partnerPower) {
      // require companion analytics for KPIs
      return !companionAnalytics;
    }
    // single-location partner requires partner dashboard
    return !adminDashboard || !companionAnalytics;
  }, [user, loading, adminDashboard, topItems, companionAnalytics]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {isGlobalLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              left: 100,
              zIndex: 10,
              backgroundColor: "rgba(211, 211, 211, 0.5)", // Semi-transparent light grey
            }}
          >
            <Loader size={40} />
          </div>
        )}
        {user.role === "admin" && adminDashboard && (
          <AdminSummaryStatistics adminDashboardData={adminDashboard} isLoading={isGlobalLoading} />
        )}
        <MDBox mt={1.5}>
          <Grid container>
            <Grid size={{ xs: 4 }}>
              {user?.role === "partner" &&
                (!!user?.partnerPower ? (
                  <MultiLocationUserPartner
                    locations={user?.locations || []}
                    selectedLocations={
                      user?.locations?.filter((loc) => selectedLocationIds.includes(loc._id)) || []
                    }
                    onChange={(vals) => setSelectedLocationIds(vals.map((v) => v._id))}
                    label="Locations (multi)"
                  />
                ) : user?.locations?.length > 0 ? (
                  <LocationUserPartner
                    defaultLocationId={locationId}
                    handleChange={handleLocationChange}
                    locations={user?.locations || {}}
                    location={location}
                  />
                ) : null)}
            </Grid>
          </Grid>
          {user.role === "partner" && companionAnalytics && (
            <PartnerSummaryStatistics
              companionAnalyticsData={companionAnalytics}
              adminDashboardData={adminDashboard || {}}
              isLoading={isGlobalLoading}
            />
          )}
          {user.role === "partner" && (
            <>
              <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize" mt={2}>
                Requests
              </MDTypography>
              <LocationRequests
                locationId={!user?.partnerPower ? locationId : undefined}
                locationIds={
                  user?.partnerPower
                    ? selectedLocationIds.length > 0
                      ? selectedLocationIds
                      : emptyLocationIds // Stable empty array reference
                    : undefined
                }
              />
            </>
          )}
        </MDBox>
        <MDBox mt={6}>
          {/* <Grid container> */}
          <Grid container spacing={user?.role === "admin" ? 3 : 12}>
            {user?.role === "admin" && adminDashboard && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="dark"
                    title="Visitors"
                    description={renderAudiReportDescription()}
                    date={
                      <div>
                        updated:{" "}
                        {adminDashboard.auReport?.timeZoneNow && (
                          <ReactTimeAgo
                            date={new Date(adminDashboard.auReport?.timeZoneNow)}
                            locale="en-US"
                          />
                        )}
                      </div>
                    }
                    chart={{
                      labels: adminDashboard.auReport?.labels,
                      datasets: { label: "Visitors", data: adminDashboard.auReport?.series[0] },
                    }}
                  />
                </MDBox>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6, md: user?.role === "admin" ? 4 : 6 }}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="success"
                  title="daily sales"
                  description={renderDescription()}
                  date={
                    adminDashboard?.dailySales && adminDashboard.dailySales.dataExist ? (
                      <div>
                        Last Order completed{" "}
                        {adminDashboard.dailySales?.lastOrder &&
                          formatDistanceToNow(new Date(adminDashboard.dailySales.lastOrder), {
                            addSuffix: true,
                          })}
                      </div>
                    ) : (
                      <div>No orders made yet</div>
                    )
                  }
                  chart={{
                    labels: adminDashboard?.dailySales?.labels,
                    datasets: { label: "Daily sales", data: adminDashboard?.dailySales?.series[0] },
                  }}
                />
              </MDBox>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: user?.role === "admin" ? 4 : 6 }}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="primary"
                  title="Restaurant Bookings"
                  description="monthly performance"
                  date={
                    <div>
                      Last booking{" "}
                      {adminDashboard?.monthlyBooking?.lastBooking && (
                        <ReactTimeAgo
                          date={new Date(adminDashboard?.monthlyBooking?.lastBooking)}
                          locale="en-US"
                        />
                      )}
                    </div>
                  }
                  chart={{
                    labels: adminDashboard?.monthlyBooking?.labels,
                    datasets: {
                      label: "monthly performance",
                      data: adminDashboard?.monthlyBooking?.series[0],
                    },
                  }}
                />
              </MDBox>
            </Grid>
          </Grid>
          <MDBox py={3}>
            <MDBox mb={3}>
              <OrdersDataTable
                locationId={user.role === "partner" && !user?.partnerPower ? locationId : undefined}
                locationIds={
                  user.role === "partner" && !!user?.partnerPower
                    ? selectedLocationIds.length > 0
                      ? selectedLocationIds
                      : undefined
                    : undefined
                }
              />
            </MDBox>
            <MDBox mb={3}>
              <BookingsDataTable
                locationId={user.role === "partner" && !user?.partnerPower ? locationId : undefined}
                locationIds={
                  user.role === "partner" && !!user?.partnerPower
                    ? selectedLocationIds.length > 0
                      ? selectedLocationIds
                      : emptyLocationIds // Stable empty array reference
                    : undefined
                }
              />
            </MDBox>
            {user.role === "admin" && topItems && (
              <MDBox mb={3}>
                <TopViewedItems topItems={topItems} />{" "}
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default Analytics;
